import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarClock,
  Target,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTran } from "@/hooks";
import { PageHeader, SelectField, FormField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import config from "@/lib/constants/config";
import {
  ORG_BOUNDARY,
  REGRESSION_MODELS,
  SBTI_TEMPERATURE,
  TARGET_TYPES,
} from "@/lib/constants";

import { useOnboardingStore } from "../hooks";
import {
  buildNxplanPayload,
  calcNxplanProgress,
  calculateTargetReduction,
  getAnnualReductionValue,
} from "../utils";
import { validateNxplanForm } from "../schemas";
import MeetingRequestDialog from "./MeetingRequestDialog";
import OnboardingWizardShell from "./OnboardingWizardShell";
import { NxplanScopeProvider } from "./NxplanScopeContext";
import Scope1Form from "./Scope1Form";
import Scope2Form from "./Scope2Form";
import Scope3Form from "./Scope3Form";

const NxplanForm = observer(
  ({
    showHeaderActions = false,
    submitTick,
    prevTick,
    skipTick,
    onValid,
    setNxplanStep,
  }) => {
    const { t } = useTran();
    const navigate = useNavigate();
    const location = useLocation();
    const isFromDashboard = location.state?.from === "dashboard";
    const {
      onboardData,
      baseYearOptions,
      targetYearOptions,
      currentYear,
      getYearOptions,
    } = useOnboardingStore();

    useEffect(() => {
      getYearOptions?.();
    }, [getYearOptions]);

    const [form, setForm] = useState({
      organizationBoundary: "",
      baseYear: "",
      reportingYear: "",
      targetYear: "",
      isSBTi: "",
      sbtiTemperature: "",
      targetReduction: "",
      targetType: "",
      regressionModel: "",
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [step, setStep] = useState(1);
    const [baseline, setBaseline] = useState({
      scope1: "",
      scope2: "",
      scope3: "",
    });
    const [accordionOpen, setAccordionOpen] = useState({
      scope1: false,
      scope2: false,
      scope3: false,
    });
    const [freq, setFreq] = useState({
      scope1: "yearly",
      scope2: "yearly",
      scope3: "yearly",
    });
    const [emissions, setEmissions] = useState({
      scope1: [""],
      scope2: [""],
      scope3: [""],
    });
    const [refFiles, setRefFiles] = useState({
      scope1: [null],
      scope2: [null],
      scope3: [null],
    });
    const [refFileName, setRefFileName] = useState({
      scope1: [""],
      scope2: [""],
      scope3: [""],
    });
    const [refFileLink, setRefFileLink] = useState({
      scope1: [""],
      scope2: [""],
      scope3: [""],
    });
    const [meetingOpen, setMeetingOpen] = useState(false);

    useEffect(() => {
      if (onboardData) {
        setForm({
          organizationBoundary: onboardData.onboard?.organizationBoundary ?? "",
          baseYear: onboardData.onboard?.baseYear ?? "",
          targetYear: onboardData.nxplan?.targetYear ?? "",
          reportingYear: onboardData.onboard?.reportingYear ?? "",
          isSBTi: onboardData.nxplan?.isSBTi ?? "",
          sbtiTemperature: onboardData.nxplan?.sbtiTemperature ?? "",
          targetReduction: onboardData.nxplan?.targetReduction ?? "",
          targetType: onboardData.nxplan?.targetType ?? "",
          regressionModel: onboardData.nxplan?.regressionModel ?? "",
        });

        if (onboardData.nxplan) {
          if (onboardData.nxplan.baselineValue)
            setBaseline(onboardData.nxplan.baselineValue);
          setFreq({
            scope1: onboardData.nxplan?.scope1Emission?.frequence || "yearly",
            scope2: onboardData.nxplan?.scope2Emission?.frequence || "yearly",
            scope3: onboardData.nxplan?.scope3Emission?.frequence || "yearly",
          });
          setEmissions({
            scope1: (onboardData.nxplan?.scope1Emission?.data || []).map(
              (item) => String(item.emission ?? ""),
            ),
            scope2: (onboardData.nxplan?.scope2Emission?.data || []).map(
              (item) => String(item.emission ?? ""),
            ),
            scope3: (onboardData.nxplan?.scope3Emission?.data || []).map(
              (item) => String(item.emission ?? ""),
            ),
          });
          setRefFileName({
            scope1: (onboardData.nxplan?.scope1Emission?.data || []).map(
              (data) => String(data.refFileName ?? ""),
            ),
            scope2: (onboardData.nxplan?.scope2Emission?.data || []).map(
              (data) => String(data.refFileName ?? ""),
            ),
            scope3: (onboardData.nxplan?.scope3Emission?.data || []).map(
              (data) => String(data.refFileName ?? ""),
            ),
          });
          setRefFileLink({
            scope1: (onboardData.nxplan?.scope1Emission?.data || []).map(
              (data) => String(data.refFileLink ?? ""),
            ),
            scope2: (onboardData.nxplan?.scope2Emission?.data || []).map(
              (data) => String(data.refFileLink ?? ""),
            ),
            scope3: (onboardData.nxplan?.scope3Emission?.data || []).map(
              (data) => String(data.refFileLink ?? ""),
            ),
          });
        }
      }
    }, [onboardData]);

    const reportingYearOptions = useMemo(() => {
      const base = form.baseYear ? Number(form.baseYear) : null;
      if (base && currentYear) {
        const range = [];
        for (let year = base; year <= currentYear; year += 1) range.push(year);
        return range;
      }
      return baseYearOptions || [];
    }, [form.baseYear, currentYear, baseYearOptions]);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const markTouched = (key) =>
      setTouched((prev) => ({ ...prev, [key]: true }));
    const handleBlur = (key) => () => markTouched(key);
    const getFieldError = (key) =>
      errors[key] && (touched[key] || submitTick > 0) ? errors[key] : undefined;

    const touchErrors = (errs) => {
      if (!errs || Object.keys(errs).length === 0) return;
      setTouched((prev) =>
        Object.keys(errs).reduce((acc, key) => ({ ...acc, [key]: true }), {
          ...prev,
        }),
      );
    };

    const runValidation = (targetStep = step) => {
      const errs = validateNxplanForm(form, { step: targetStep, baseline, t });
      setErrors(errs);
      touchErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const errorSummary = useMemo(() => {
      if (submitTick <= 0) return [];
      const messages = Object.values(errors).filter(
        (msg) => typeof msg === "string" && msg.trim().length > 0,
      );
      return Array.from(new Set(messages));
    }, [errors, submitTick]);

    const progress = useMemo(
      () => calcNxplanProgress({ form, freq, emissions, baseline }),
      [form, freq, emissions, baseline],
    );

    const pct = Math.max(0, Math.min(100, Number(progress) || 0));

    const progressFooter = (
      <div className="flex items-center gap-3">
        <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-foreground">{pct}%</span>
      </div>
    );

    const navSteps = [
      { id: 1, title: t("Boundary & Timeline"), icon: Building2 },
      { id: 2, title: t("Reporting Year"), icon: Calendar },
      { id: 3, title: t("Targets & Baseline"), icon: Target },
    ];

    const sanitizeNumeric = (value) => {
      const cleaned = String(value ?? "").replace(/[^\d.]/g, "");
      const [head, ...rest] = cleaned.split(".");
      return rest.length ? `${head}.${rest.join("")}` : head;
    };

    const calculatedTargetReduction = calculateTargetReduction(
      form.isSBTi,
      form.targetYear,
      form.baseYear,
      form.sbtiTemperature,
      form.regressionModel,
      form.targetReduction,
    );

    useEffect(() => {
      if (submitTick <= 0) return;
      if (step === 1) {
        if (runValidation(1)) setStep(2);
        return;
      }
      if (step === 2) {
        if (runValidation(2)) setStep(3);
        return;
      }
      if (runValidation(3)) {
        const payload = buildNxplanPayload({
          form,
          freq,
          emissions,
          baseline,
          refFiles,
          refFileName,
          refFileLink,
          progress,
        });
        onValid?.(payload);
      }
    }, [submitTick]);

    useEffect(() => {
      setNxplanStep?.(step);
    }, [step, setNxplanStep]);

    useEffect(() => {
      if (prevTick > 0) {
        setStep((s) => (s > 1 ? s - 1 : s));
      }
    }, [prevTick]);

    useEffect(() => {
      if (skipTick > 0) {
        setStep((s) => (s < 3 ? s + 1 : s));
      }
    }, [skipTick]);

    const headerActions = showHeaderActions ? (
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMeetingOpen(true)}
          title={t("Request a meeting")}
          className="gap-2 rounded-full"
        >
          <span>{t("Request Meeting")}</span>
          <span className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-foreground">
            <CalendarClock className="h-4 w-4" />
          </span>
        </Button>
      </div>
    ) : null;

    const absolutePath = `${config.baseUrl.replace(/\/$/, "")}/static`;
    const scopeContextValue = {
      accordionOpen,
      setAccordionOpen,
      freq,
      setFreq,
      emissions,
      setEmissions,
      refFiles,
      setRefFiles,
      refFileName,
      refFileLink,
      absolutePath,
      sanitizeNumeric,
    };

    return (
      <div className="space-y-6">
        <PageHeader
          title={t("NXPlan - Carbon Neutral Planning Setup")}
          description={t(
            "Plan your neutrality targets, reporting cadence, and baseline emissions.",
          )}
          actions={headerActions}
        />
        {progressFooter}

        <MeetingRequestDialog
          open={meetingOpen}
          onOpenChange={setMeetingOpen}
        />

        <OnboardingWizardShell
          navItems={navSteps.map((stepItem) => ({
            ...stepItem,
            isActive: stepItem.id === step,
            isDone: stepItem.id < step,
            onClick: () =>
              setStep((current) =>
                progress === 100
                  ? stepItem.id
                  : stepItem.id < current
                    ? stepItem.id
                    : current,
              ),
          }))}
        >
          {errorSummary.length > 0 && (
            <div className="mb-4 rounded-radius border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p className="font-medium">
                {t("Please fix the highlighted fields to continue.")}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {errorSummary.slice(0, 4).map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <SelectField
                    id="organizationBoundary"
                    label={t("What is your organization boundary?")}
                    placeholder={t("Select boundary type")}
                    value={form.organizationBoundary}
                    onValueChange={(value) =>
                      update({ organizationBoundary: value })
                    }
                    onBlur={handleBlur("organizationBoundary")}
                    options={ORG_BOUNDARY.map((item) => ({
                      key: item.key,
                      label: t(item.value),
                    }))}
                    error={getFieldError("organizationBoundary")}
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(
                      "Choose how your organization consolidates emissions (e.g., operational control).",
                    )}
                  </p>
                </div>
                <div>
                  <SelectField
                    id="baseYear"
                    label={t("What is the base year?")}
                    placeholder={t("Select base year")}
                    value={form.baseYear ? String(form.baseYear) : ""}
                    onValueChange={(value) => update({ baseYear: value })}
                    onBlur={handleBlur("baseYear")}
                    options={(baseYearOptions || []).map((year) => ({
                      key: String(year),
                      label: year,
                    }))}
                    error={getFieldError("baseYear")}
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("Baseline year used for planning reductions.")}
                  </p>
                </div>
              </div>

              <div>
                <SelectField
                  id="targetYear"
                  label={t(
                    "What is your target timeline for achieving carbon neutrality?",
                  )}
                  placeholder={t("Select target year")}
                  value={form.targetYear ? String(form.targetYear) : ""}
                  onValueChange={(value) => update({ targetYear: value })}
                  onBlur={handleBlur("targetYear")}
                  options={(targetYearOptions || []).map((year) => ({
                    key: String(year),
                    label: year,
                  }))}
                  error={getFieldError("targetYear")}
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(
                    "Pick a future year that aligns with your neutrality goals.",
                  )}
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <SelectField
                  id="reportingYear"
                  label={t("What is your reporting year?")}
                  placeholder={t("Select reporting year")}
                  value={form.reportingYear ? String(form.reportingYear) : ""}
                  onValueChange={(value) => update({ reportingYear: value })}
                  onBlur={handleBlur("reportingYear")}
                  options={(reportingYearOptions || []).map((year) => ({
                    key: String(year),
                    label: year,
                  }))}
                  error={getFieldError("reportingYear")}
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("Select the year that this report will cover.")}
                </p>
              </div>

              <NxplanScopeProvider value={scopeContextValue}>
                <div className="space-y-3">
                  <Scope1Form />
                  <Scope2Form />
                  <Scope3Form />
                </div>
              </NxplanScopeProvider>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">
                  {t("SBTi Target Setting")}
                </Label>
                <div
                  className={`rounded-radius border p-3 ${
                    errors.isSBTi && (touched.isSBTi || submitTick > 0)
                      ? "border-destructive"
                      : "border-border"
                  }`}
                >
                  <RadioGroup
                    value={form.isSBTi}
                    onValueChange={(value) => {
                      if (value === "no") {
                        update({
                          isSBTi: value,
                          sbtiTemperature: "",
                          targetType: "",
                          regressionModel: "",
                        });
                      } else {
                        update({ isSBTi: value });
                      }
                    }}
                    className="flex flex-wrap items-center gap-4"
                  >
                    {["yes", "no"].map((value) => (
                      <div key={value} className="flex items-center gap-2">
                        <RadioGroupItem id={`sbti-${value}`} value={value} />
                        <Label htmlFor={`sbti-${value}`} className="uppercase">
                          {value}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t(
                      "Will your organization align targets with the Science Based Targets initiative (SBTi)?",
                    )}
                  </p>
                </div>
                {getFieldError("isSBTi") && (
                  <p className="mt-1 text-xs text-destructive">
                    {getFieldError("isSBTi")}
                  </p>
                )}

                {form.isSBTi === "yes" && (
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SelectField
                      id="sbtiTemperature"
                      label={t("SBTi Temperature")}
                      placeholder={t("Select type")}
                      value={
                        form.sbtiTemperature ? String(form.sbtiTemperature) : ""
                      }
                      onValueChange={(value) =>
                        update({ sbtiTemperature: value })
                      }
                      onBlur={handleBlur("sbtiTemperature")}
                      options={SBTI_TEMPERATURE.map((item) => ({
                        key: item.key,
                        label: item.value,
                      }))}
                      error={getFieldError("sbtiTemperature")}
                      required
                    />
                    <SelectField
                      id="targetType"
                      label={t("Target Reduction Type")}
                      placeholder={t("Select type")}
                      value={form.targetType}
                      onValueChange={(value) => update({ targetType: value })}
                      onBlur={handleBlur("targetType")}
                      options={TARGET_TYPES.map((item) => ({
                        key: item.key,
                        label: item.value,
                      }))}
                      error={getFieldError("targetType")}
                      required
                    />
                  </div>
                )}

                {form.isSBTi === "yes" && (
                  <div className="mt-4">
                    <SelectField
                      id="regressionModel"
                      label={t("Absolute Reduction Model")}
                      placeholder={t("Select model")}
                      value={form.regressionModel}
                      onValueChange={(value) =>
                        update({ regressionModel: value })
                      }
                      onBlur={handleBlur("regressionModel")}
                      options={REGRESSION_MODELS.filter(
                        (item) => item.type === form.targetType,
                      ).map((item) => ({
                        key: item.key,
                        label: item.value,
                      }))}
                      error={getFieldError("regressionModel")}
                      required
                    />
                  </div>
                )}

                <div
                  className={`mt-4 ${
                    form.isSBTi === "yes" && form.regressionModel
                      ? "grid grid-cols-1 gap-4 md:grid-cols-2"
                      : ""
                  }`}
                >
                  {form.isSBTi === "yes" && form.regressionModel && (
                    <FormField
                      label={t("Annual Reduction Rate (%)")}
                      disabled
                      type="text"
                      inputMode="decimal"
                      value={Number(
                        getAnnualReductionValue(
                          form.sbtiTemperature,
                          form.regressionModel,
                        ),
                      ).toFixed(2)}
                    />
                  )}
                  <FormField
                    label={t("Target Reduction (%)")}
                    type="text"
                    inputMode="decimal"
                    disabled={form.isSBTi === "yes"}
                    value={
                      form.isSBTi === "yes" && form.regressionModel
                        ? Number(calculatedTargetReduction).toFixed(2)
                        : form.isSBTi === "no"
                          ? form.targetReduction
                          : 0
                    }
                    onChange={(event) =>
                      update({
                        targetReduction: sanitizeNumeric(event.target.value),
                      })
                    }
                    onBlur={handleBlur("targetReduction")}
                    error={getFieldError("targetReduction")}
                    placeholder={t("Enter percentage (0-100)")}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(
                    "Provide your planned reduction target as a percentage of baseline emissions.",
                  )}
                </p>
              </div>

              <div className="overflow-hidden rounded-radius border border-border">
                <div className="grid grid-cols-2 bg-muted/40 px-4 py-2 text-sm font-semibold text-foreground">
                  <div>{t("Scope")}</div>
                  <div>{t("Baseline")}</div>
                </div>
                {[
                  { key: "scope1", label: "Scope 1 (tCO₂e)" },
                  { key: "scope2", label: "Scope 2 (tCO₂e)" },
                  { key: "scope3", label: "Scope 3 (tCO₂e)" },
                ].map((row) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-2 items-center border-t border-border px-4 py-3"
                  >
                    <div className="text-sm text-foreground">{row.label}</div>
                    <FormField
                      id={`baseline-${row.key}`}
                      type="text"
                      inputMode="decimal"
                      placeholder={t("Enter Emission")}
                      value={baseline[row.key]}
                      onChange={(event) =>
                        setBaseline((prev) => ({
                          ...prev,
                          [row.key]: sanitizeNumeric(event.target.value),
                        }))
                      }
                      onBlur={() => markTouched(`baseline_${row.key}`)}
                      error={getFieldError(`baseline_${row.key}`)}
                    />
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-3">
                  <div className="text-sm font-semibold text-foreground">
                    {t("Target Reduction Amount (tCO₂e)")}
                  </div>
                  <div className="text-sm font-bold text-foreground">
                    {(() => {
                      const sum = ["scope1", "scope2", "scope3"].reduce(
                        (acc, key) => acc + (parseFloat(baseline[key]) || 0),
                        0,
                      );
                      let pctValue = 0;
                      if (form.isSBTi === "no")
                        pctValue = parseFloat(form.targetReduction) || 0;
                      else if (form.isSBTi === "yes")
                        pctValue = parseFloat(calculatedTargetReduction) || 0;
                      const amount = sum * (pctValue / 100);
                      return Number.isFinite(amount)
                        ? amount.toFixed(3)
                        : "0.000";
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </OnboardingWizardShell>
        {/* 
        {isFromDashboard && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("Back")}
          </Button>
        )} */}
      </div>
    );
  },
);

export default NxplanForm;
